import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useSaveAssessmentsAnswersMutation } from 'services/assessmentsService';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { AssessmentsContext } from '../contexts/AssessmentsContext';
import {
  formatToQuestionnaireResponse,
  getAnsweredQuestionIdsFromResponse,
} from '../utils/assessmentsAnswersUtils';
import { useAssessmentsQuestions } from './useAssessmentsQuestions';
import { useAssessmentsAlertError } from './useAssessmentsAlertError';
import { selectActivityId, selectClientPatientId } from '../assessmentsAuthSlice';
import {
  selectPreviouslyAnsweredQuestionIds,
  selectSkippedQuestionNumbers,
  setAssessmentSubmissionState,
  setPreviouslyAnsweredQuestionIds,
  setSaveAndCloseCompleted,
  setSaveCompleted,
} from '../assessmentsSlice';
import {
  ASSESSMENTS_AUTO_SAVE_DEBOUNCE_MS,
  ERROR_FLARE_TIMEOUT_MS,
  AssessmentsNativeOutboundEventType,
} from '../constants';
import { useAssessmentsParams } from './useAssessmentsParams';
import { AssessmentStatus } from '../types';
import { validateAssessmentsAnswers } from '../utils/assessmentsAnswersValidation.utils';
import { isServerError } from '../utils/assessmentsErrorUtils';
import { notifyNativeApp } from '../utils/nativeBridge';

export const useAssessmentsAnswers = () => {
  const dispatch = useDispatch();

  const { questions, assessmentsQuestionsResponse } = useAssessmentsQuestions();
  const { showAlert } = useAssessmentsAlertError();
  const activityId = useSelector(selectActivityId);
  const clientPatientId = useSelector(selectClientPatientId);
  const { tenantId, surveyEventId } = useAssessmentsParams();
  const [autoSaved, setAutoSaved] = useState(false);
  const [submittingAnswers, setSubmittingAnswers] = useState(false);
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [lastError, setLastError] = useState<{ message: string; status?: number } | null>(null);
  const skippedQuestions = useSelector(selectSkippedQuestionNumbers);
  const previouslyAnsweredQuestionIds = useSelector(selectPreviouslyAnsweredQuestionIds);
  // Absent for campaign assessments, which have nothing to do after a submit
  const onSubmitSuccess = useContext(AssessmentsContext)?.onSubmitSuccess;

  const errorTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (errorTimer.current) {
        clearTimeout(errorTimer.current);
      }
    },
    [],
  );

  const [saveAssessmentsAnswers] = useSaveAssessmentsAnswersMutation();

  const saveAnswers = useCallback(
    (data: FieldValues, userInitiated: boolean = false, status: AssessmentStatus) => {
      if (!assessmentsQuestionsResponse || !activityId || !clientPatientId || !tenantId) {
        return Promise.reject(new Error('Missing required data for submitting answers'));
      }
      // Todo : Refactor the manual edit in future to BFF and remove the manual edit here.
      const questionnaireResponse = formatToQuestionnaireResponse({
        questions,
        answers: data,
        assessmentsId: `Questionnaire/${assessmentsQuestionsResponse?.surveyId}`,
        activityId,
        status,
        clientPatientId: `Patient/${clientPatientId}`,
        skippedQuestionNumbers: skippedQuestions,
        previouslyAnsweredQuestionIds,
      });

      const request = {
        tenantId,
        requestBody: {
          userInitiated,
          questionnaireResponse,
        },
      };

      return saveAssessmentsAnswers(request).then((res) => {
        if (res.error) {
          // Extract status code from RTK Query error
          const errorStatus = (res.error as any)?.status;
          if (errorStatus && isServerError(errorStatus)) {
            showAlert('Something went wrong. Please try again later.');
          }
          const errorMessage =
            (res.error as any)?.data?.message || 'Something went wrong. Please try again later.';
          setLastError({ message: errorMessage, status: errorStatus });
          return Promise.reject(res.error);
        }
        setLastError(null); // Clear error on success
        // Keep in sync with the server so a later clear of this answer is detected
        dispatch(
          setPreviouslyAnsweredQuestionIds(
            getAnsweredQuestionIdsFromResponse(questionnaireResponse),
          ),
        );
        return res.data;
      });
    },
    [
      assessmentsQuestionsResponse,
      activityId,
      clientPatientId,
      tenantId,
      questions,
      skippedQuestions,
      previouslyAnsweredQuestionIds,
      saveAssessmentsAnswers,
      dispatch,
    ],
  );

  const validateAnswers = useCallback(
    (values: FieldValues) => {
      const validationResult = validateAssessmentsAnswers(
        questions ?? [],
        values,
        skippedQuestions,
      );
      if (validationResult?.isInvalidDate) {
        dispatch(
          setAssessmentSubmissionState({
            state: 'required-skipped',
            requiredUnansweredQuestion: validationResult.questionId,
            unansweredQuestion: null,
          }),
        );
        return;
      }
      if (validationResult?.isRequired) {
        dispatch(
          setAssessmentSubmissionState({
            state: 'required-skipped',
            requiredUnansweredQuestion: validationResult.questionId,
            unansweredQuestion: null,
          }),
        );
        return;
      }
      if (validationResult?.isUnanswered) {
        dispatch(
          setAssessmentSubmissionState({
            state: 'optional-skipped',
            requiredUnansweredQuestion: null,
            unansweredQuestion: validationResult.questionId,
          }),
        );
        return;
      }
      dispatch(
        setAssessmentSubmissionState({
          state: 'completed',
          requiredUnansweredQuestion: null,
          unansweredQuestion: null,
        }),
      );
    },
    [questions, skippedQuestions, dispatch],
  );

  const submitAnswers = useCallback(
    (values: FieldValues) => {
      setSubmittingAnswers(true);
      saveAnswers(values, true, 'completed')
        .then(() => {
          dispatch(setSaveCompleted(true));
          notifyNativeApp({
            type: AssessmentsNativeOutboundEventType.ASSESSMENT_SUBMITTED,
            tenantId,
            surveyEventId,
          });
          onSubmitSuccess?.();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to submit answers:', error);
        })
        .finally(() => {
          setSubmittingAnswers(false);
        });
    },
    [saveAnswers, dispatch, tenantId, surveyEventId, onSubmitSuccess],
  );

  const saveAndCloseAnswers = useCallback(
    (fieldValues: FieldValues, eventId?: string) => {
      setSavingAnswers(true);
      saveAnswers(fieldValues, true, 'in-progress')
        .then(() => {
          dispatch(setSaveAndCloseCompleted(true));
          notifyNativeApp({
            type: AssessmentsNativeOutboundEventType.ASSESSMENT_SAVED_AND_CLOSED,
            tenantId,
            surveyEventId,
            ...(eventId !== undefined ? { eventId } : {}),
          });
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to save and close answers:', error);
        })
        .finally(() => {
          setSavingAnswers(false);
        });
    },
    [saveAnswers, dispatch, tenantId, surveyEventId],
  );

  const saveAndCloseAnswersOnTimeout = useCallback(
    (fieldValues: FieldValues) => {
      setSavingAnswers(true);
      // Use of .then callback is to leave the scope for
      // addition of any other UI effect in case needed
      saveAnswers(fieldValues, true, 'in-progress')
        .then(() => {
          dispatch(setSaveAndCloseCompleted(true));
          notifyNativeApp({
            type: AssessmentsNativeOutboundEventType.ASSESSMENT_SAVED_AND_CLOSED,
            tenantId,
            surveyEventId,
          });
        })
        .catch(() => {
          // specifically done for timeout scenario where we want to ensure that
          // even if save fails, we still allow user to exit the assessment.
          dispatch(setSaveAndCloseCompleted(true));
        })
        .finally(() => {
          setSavingAnswers(false);
        });
    },
    [saveAnswers, dispatch, tenantId, surveyEventId],
  );

  const autoSaveAnswers = useCallback(
    (fieldValues: FieldValues, onSuccess?: () => void) => {
      setAutoSaved(false);
      return saveAnswers(fieldValues, false, 'in-progress')
        .then((result) => {
          setAutoSaved(true);
          if (errorTimer.current) {
            clearTimeout(errorTimer.current);
          }
          errorTimer.current = setTimeout(() => {
            setAutoSaved(false);
          }, ERROR_FLARE_TIMEOUT_MS);
          // Call the optional success callback (e.g., to reset dirty state)
          if (onSuccess) {
            onSuccess();
          }
          return result;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Auto-save failed:', error);
          throw error;
        });
    },
    [saveAnswers],
  );

  const debouncedAutoSave = useDebouncedCallback(
    autoSaveAnswers,
    ASSESSMENTS_AUTO_SAVE_DEBOUNCE_MS,
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  return {
    saveAndCloseAnswers,
    saveAndCloseAnswersOnTimeout,
    submitAnswers,
    autoSaveAnswers: debouncedAutoSave,
    autoSaveAnswersImmediate: autoSaveAnswers,
    autoSaved,
    submittingAnswers,
    savingAnswers,
    validateAnswers,
    lastError,
    clearError,
  };
};
